import { type CSSProperties } from 'react';

import { type ShortUrlResponse } from '@air/api/types';

/** The wire union for `presentationSettings.thumbnailRadius` — option values on authoring surfaces mirror it verbatim. */
export type ShareLinkThumbnailRadius = NonNullable<
  NonNullable<ShortUrlResponse['presentationSettings']>['thumbnailRadius']
>;

/*
 * The absent-value fallback for `presentationSettings.thumbnailRadius`. This MUST equal what the
 * public renderers show when the field is absent: links saved before the field existed have no
 * `thumbnailRadius` and must keep rendering the original corner radii (the rounded-12 page chrome
 * and the native gallery card radii). The authoring surfaces default from the same constant so the
 * editor never shows a selection that differs from what a legacy link actually renders. Lives in
 * the shared share-link-design folder (not an authoring feature folder) so the public viewers don't
 * import from an authoring feature tree.
 */
export const THUMBNAIL_RADIUS_DEFAULT: ShareLinkThumbnailRadius = 'standard';

/*
 * Page-chrome corner classes, keyed by the full wire union so a new union member is a type error
 * here — the maps and the renderers can't drift apart. The `standard` rows MUST stay byte-identical
 * to the classes the public share pages shipped with before the field existed (the rounded-12
 * family), so an absent value is a true no-op.
 */
const SHARE_LINK_PAGE_CORNER_CLASSES: Record<ShareLinkThumbnailRadius, string> = {
  standard: 'rounded-12',
  subtle: 'rounded-8',
  square: 'rounded-none',
};

const SHARE_LINK_PAGE_TOP_CORNER_CLASSES: Record<ShareLinkThumbnailRadius, string> = {
  standard: 'rounded-t-12',
  subtle: 'rounded-t-8',
  square: 'rounded-t-none',
};

const SHARE_LINK_PAGE_BOTTOM_CORNER_CLASSES: Record<ShareLinkThumbnailRadius, string> = {
  standard: 'rounded-b-12',
  subtle: 'rounded-b-8',
  square: 'rounded-b-none',
};

const getCornerClass = (
  cornerClasses: Record<ShareLinkThumbnailRadius, string>,
  thumbnailRadius: ShareLinkThumbnailRadius | undefined,
): string => {
  // The wire value can outrun the compiled union (backend ships a new thumbnailRadius option before
  // this frontend deploys), so an unmapped runtime value must resolve to a real class — never to
  // `undefined`, which would leave the page chrome cornerless. TS considers the trailing `??`
  // unreachable (the maps are total over the union); it exists purely for that runtime skew.
  const cornerClass: string | undefined = cornerClasses[thumbnailRadius ?? THUMBNAIL_RADIUS_DEFAULT];

  return cornerClass ?? cornerClasses[THUMBNAIL_RADIUS_DEFAULT];
};

/** All-corner radius for the public share page's content column — follows the Corner rounding design setting. */
export const getShareLinkPageCornerClass = (thumbnailRadius: ShareLinkThumbnailRadius | undefined): string =>
  getCornerClass(SHARE_LINK_PAGE_CORNER_CLASSES, thumbnailRadius);

/** Top-only radius for the cover band's inset variant — matches the content column's corners. */
export const getShareLinkPageTopCornerClass = (thumbnailRadius: ShareLinkThumbnailRadius | undefined): string =>
  getCornerClass(SHARE_LINK_PAGE_TOP_CORNER_CLASSES, thumbnailRadius);

/** Bottom-only radius for the sticky bottom bar's clip wrapper — matches the content column's corners. */
export const getShareLinkPageBottomCornerClass = (thumbnailRadius: ShareLinkThumbnailRadius | undefined): string =>
  getCornerClass(SHARE_LINK_PAGE_BOTTOM_CORNER_CLASSES, thumbnailRadius);

// React's CSSProperties has no index signature for custom properties, so the vars must be named
// explicitly for the style object to typecheck without an assertion.
type GalleryCardRadiusStyle = CSSProperties & {
  '--gallery-card-radius': string;
  '--gallery-card-inner-radius': string;
};

/*
 * Gallery card radius vars, keyed by the full wire union so a new union member is a type error
 * here. The shared gallery card components (GalleryAsset/GalleryBoard shells, their thumbnail
 * internals) read these vars with their original Tailwind px values as fallbacks (e.g.
 * `rounded-[var(--gallery-card-radius,16px)]`), so every surface that never sets the vars —
 * private boards, search — keeps rendering its native radii. Both public share pages set the vars
 * (PublicShowcasePage and PublicBoardPage host them on their page roots). `standard` is
 * deliberately `undefined`: setting no vars keeps the public collection and board share pages on
 * those exact native radii too, making the default a true no-op.
 *
 * Concentric corners: the cards keep a 4px gap (p-1 / inset-1) between the outer shell and the
 * inner thumbnail, so the inner radius MUST be outer − 4px for both arcs to share a center.
 */
const GALLERY_CARD_RADIUS_STYLES: Record<ShareLinkThumbnailRadius, GalleryCardRadiusStyle | undefined> = {
  standard: undefined,
  subtle: { '--gallery-card-radius': '8px', '--gallery-card-inner-radius': '4px' },
  square: { '--gallery-card-radius': '0px', '--gallery-card-inner-radius': '0px' },
};

// Set the returned style on the page's root div (which already hosts --share-link-page-max-width)
// so every gallery card inside the content column reads the same vars without prop threading.
// A wire value newer than the compiled union resolves to `undefined` — no vars set — which renders
// the native radii: the safe degradation for corner styling, unlike the page-chrome classes above
// which must always emit a class.
export const getGalleryCardRadiusStyle = (
  thumbnailRadius: ShareLinkThumbnailRadius | undefined,
): GalleryCardRadiusStyle | undefined => GALLERY_CARD_RADIUS_STYLES[thumbnailRadius ?? THUMBNAIL_RADIUS_DEFAULT];
